import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar,
  Plus,
  Trash2,
  Edit,
  Sun,
  Building,
  Repeat,
  Loader2,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { DateInput } from '@/components/ui/date-input';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// UAE Public Holidays 2026 (pre-defined for quick add)
const UAE_HOLIDAYS_2026 = [
  { name: 'New Year\'s Day', date: '2026-01-01' },
  { name: 'Eid Al Fitr (expected)', date: '2026-03-30' },
  { name: 'Eid Al Fitr Holiday', date: '2026-03-31' },
  { name: 'Eid Al Fitr Holiday', date: '2026-04-01' },
  { name: 'Eid Al Adha (expected)', date: '2026-06-06' },
  { name: 'Eid Al Adha Holiday', date: '2026-06-07' },
  { name: 'Eid Al Adha Holiday', date: '2026-06-08' },
  { name: 'Eid Al Adha Holiday', date: '2026-06-09' },
  { name: 'Islamic New Year (expected)', date: '2026-06-26' },
  { name: 'Prophet\'s Birthday (expected)', date: '2026-09-04' },
  { name: 'Commemoration Day', date: '2026-12-01' },
  { name: 'UAE National Day', date: '2026-12-02' },
  { name: 'UAE National Day Holiday', date: '2026-12-03' },
];

const HolidayManagementPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [holidays, setHolidays] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    type: 'public',
    is_recurring: false
  });

  const fetchHolidays = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/hr/holidays?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHolidays(response.data.holidays);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  const handleAddHoliday = async () => {
    if (!formData.name || !formData.date) {
      toast.error('Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/hr/holidays?creator_id=${user.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Holiday added successfully');
      setAddDialogOpen(false);
      setFormData({ name: '', date: '', type: 'public', is_recurring: false });
      fetchHolidays();
    } catch (error) {
      console.error('Error adding holiday:', error);
      toast.error('Failed to add holiday');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHoliday = async (holidayId) => {
    if (!confirm('Are you sure you want to delete this holiday?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/hr/holidays/${holidayId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Holiday deleted');
      fetchHolidays();
    } catch (error) {
      console.error('Error deleting holiday:', error);
      toast.error('Failed to delete holiday');
    }
  };

  const handleBulkAddUAEHolidays = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      for (const holiday of UAE_HOLIDAYS_2026) {
        await axios.post(
          `${API_BASE_URL}/hr/holidays?creator_id=${user.id}`,
          { ...holiday, type: 'public', is_recurring: false },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success(`Added ${UAE_HOLIDAYS_2026.length} UAE holidays`);
      setBulkDialogOpen(false);
      fetchHolidays();
    } catch (error) {
      console.error('Error adding holidays:', error);
      toast.error('Failed to add holidays');
    } finally {
      setSaving(false);
    }
  };

  // Group holidays by month
  const holidaysByMonth = holidays.reduce((acc, holiday) => {
    const month = new Date(holiday.date).toLocaleString('default', { month: 'long' });
    if (!acc[month]) acc[month] = [];
    acc[month].push(holiday);
    return acc;
  }, {});

  const TypeBadge = ({ type }) => {
    const config = {
      public: { color: 'bg-green-500/20 text-green-400', icon: Sun, label: 'Public' },
      company: { color: 'bg-blue-500/20 text-blue-400', icon: Building, label: 'Company' }
    };
    const { color, icon: Icon, label } = config[type] || config.public;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${color}`}>
        <Icon className="h-3 w-3" />
        {label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="holiday-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">Holiday Management</h1>
          <p className="text-wt-text-muted">{holidays.length} holidays in {selectedYear}</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-32 bg-wt-surface border-wt-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-wt-surface border-wt-border">
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
              <SelectItem value="2027">2027</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => setBulkDialogOpen(true)}
            className="border-wt-border text-wt-text-secondary hover:text-wt-gold"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Add UAE Holidays
          </Button>
          <Button
            onClick={() => setAddDialogOpen(true)}
            className="bg-wt-gold hover:bg-wt-gold-hover text-black"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Holiday
          </Button>
        </div>
      </div>

      {/* Holidays List by Month */}
      {Object.keys(holidaysByMonth).length === 0 ? (
        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-wt-text-muted opacity-50" />
            <p className="text-wt-text-muted">No holidays configured for {selectedYear}</p>
            <Button
              variant="outline"
              onClick={() => setBulkDialogOpen(true)}
              className="mt-4 border-wt-border"
            >
              Add UAE Public Holidays
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(holidaysByMonth).map(([month, monthHolidays]) => (
            <Card key={month} className="bg-wt-surface border-wt-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-wt-text-primary text-lg">{month}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {monthHolidays.map((holiday) => (
                    <motion.div
                      key={holiday.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex items-center justify-between p-3 rounded-lg bg-wt-bg border border-wt-border"
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-center min-w-16">
                          <p className="text-2xl font-bold text-wt-gold">
                            {new Date(holiday.date).getDate()}
                          </p>
                          <p className="text-xs text-wt-text-muted">
                            {new Date(holiday.date).toLocaleString('default', { weekday: 'short' })}
                          </p>
                        </div>
                        <div>
                          <p className="font-medium text-wt-text-primary">{holiday.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <TypeBadge type={holiday.type} />
                            {holiday.is_recurring && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400">
                                <Repeat className="h-3 w-3" />
                                Recurring
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteHoliday(holiday.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Holiday Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Add Holiday</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Holiday Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., UAE National Day"
                className="bg-wt-bg border-wt-border"
              />
            </div>

            <div className="space-y-2">
              <Label>Date</Label>
              <DateInput
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="bg-wt-bg border-wt-border"
              />
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={formData.type}
                onValueChange={(v) => setFormData({...formData, type: v})}
              >
                <SelectTrigger className="bg-wt-bg border-wt-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-surface border-wt-border">
                  <SelectItem value="public">Public Holiday</SelectItem>
                  <SelectItem value="company">Company Holiday</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-wt-bg border border-wt-border">
              <div>
                <p className="text-sm font-medium text-wt-text-primary">Recurring Annually</p>
                <p className="text-xs text-wt-text-muted">Automatically add this holiday every year</p>
              </div>
              <Switch
                checked={formData.is_recurring}
                onCheckedChange={(checked) => setFormData({...formData, is_recurring: checked})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddDialogOpen(false)}
              className="border-wt-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddHoliday}
              className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Holiday'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Add UAE Holidays Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Add UAE Public Holidays 2026</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-wt-text-muted">
              This will add {UAE_HOLIDAYS_2026.length} UAE public holidays for 2026:
            </p>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {UAE_HOLIDAYS_2026.map((holiday, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 px-3 rounded bg-wt-bg">
                  <span className="text-sm text-wt-text-primary">{holiday.name}</span>
                  <span className="text-xs text-wt-text-muted">{holiday.date}</span>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkDialogOpen(false)}
              className="border-wt-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkAddUAEHolidays}
              className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                `Add ${UAE_HOLIDAYS_2026.length} Holidays`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HolidayManagementPage;
